import styled from 'styled-components';

// Helper function to get shadow style like other card components
const getShadowStyle = (theme) => {
  if (!theme?.shadow_color) return '0 4px 8px rgba(0, 0, 0, 0.1)';

  const hexToRgba = (hex, alpha = 1) => {
    const [r, g, b] = hex.match(/\w\w/g).map(x => parseInt(x, 16));
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const softShadowColor = hexToRgba(theme.shadow_color, 0.1);
  const hardShadowColor = hexToRgba(theme.shadow_color, 0.5);

  switch (theme?.card_style) {
    case 'soft-shadow-squared':
    case 'soft-shadow-slightly-rounded':
      return `0 6px 12px ${softShadowColor}`;
    case 'hard-shadow-squared':
    case 'hard-shadow-slightly-rounded':
      return `3px 3px 0 ${hardShadowColor}`;
    case 'outline-squared':
    case 'outline-slightly-rounded':
      return 'none';
    default:
      return `0 4px 8px ${softShadowColor}`;
  }
};

export const CourseCardContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-bottom: 24px;
    margin-left: auto;
    margin-right: auto;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    cursor: pointer;
    position: relative;
    
    /* Apply theme-based styling with modern touches */
    box-shadow: ${({ theme }) => getShadowStyle(theme)};
    border-radius: ${({ theme }) =>
        theme?.card_style?.includes('slightly-rounded') ? '16px' :
        theme?.card_style?.includes('squared') ? '0' : '20px'};
    border: ${({ theme }) =>
        theme?.card_style?.includes('outline') ? `2px solid ${theme?.card_color || '#000'}` : 'none'};
    background-color: ${({ theme }) =>
        theme?.card_style?.includes('outline') ? 'transparent' : theme?.card_color || '#ffffff'};
    overflow: hidden;
    
    /* Modern elevation effect */
    &:hover {
        transform: translateY(-4px);
        box-shadow: ${({ theme }) => {
            if (!theme?.shadow_color) return '0 12px 24px rgba(0, 0, 0, 0.15), 0 4px 8px rgba(0, 0, 0, 0.1)';
            const hexToRgba = (hex, alpha = 1) => {
                const [r, g, b] = hex.match(/\w\w/g).map(x => parseInt(x, 16));
                return `rgba(${r}, ${g}, ${b}, ${alpha})`;
            };
            const shadowColor = hexToRgba(theme.shadow_color, 0.15);
            const shadowColorLight = hexToRgba(theme.shadow_color, 0.08);
            return `0 12px 24px ${shadowColor}, 0 4px 8px ${shadowColorLight}`;
        }};
    }
`;

export const CourseImageSection = styled.div`
    aspect-ratio: 16/9;
    position: relative;
    padding: 16px;
    border-radius: ${({ theme }) =>
        theme?.card_style?.includes('slightly-rounded') ? '16px 16px 0 0' :
        theme?.card_style?.includes('squared') ? '0' : '20px 20px 0 0'};
    
    .course-thumbnail {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
        transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        border-radius: ${({ theme }) =>
            theme?.card_style?.includes('slightly-rounded') ? '12px' :
            theme?.card_style?.includes('squared') ? '8px' : '16px'};
    }
    
    /* Modern image overlay for better text readability */
    .image-overlay {
        position: absolute;
        bottom: 14px;
        left: 16px;
        right: 16px;
        height: calc(60% - 16px);
        background: linear-gradient(to top, rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0));
        z-index: 1;
        pointer-events: none;
        border-radius: ${({ theme }) =>
            theme?.card_style?.includes('slightly-rounded') ? '0 0 12px 12px' :
            theme?.card_style?.includes('squared') ? '0 0 8px 8px' : '0 0 16px 16px'};
        transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    }
    
    /* Subtle hover effect for thumbnail and overlay together */
    &:hover .course-thumbnail {
        transform: scale(1.02);
    }
    
    &:hover .image-overlay {
        transform: scale(1.02);
    }
    
    /* Enhanced fallback design */
    .course-hero-bg {
        width: 100%;
        height: 100%;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #ff9a9e 100%);
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: ${({ theme }) =>
            theme?.card_style?.includes('slightly-rounded') ? '12px' :
            theme?.card_style?.includes('squared') ? '8px' : '16px'};
    }
    
    .gradient-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(135deg, 
            rgba(102, 126, 234, 0.9) 0%, 
            rgba(118, 75, 162, 0.85) 50%, 
            rgba(255, 154, 158, 0.8) 100%
        );
        border-radius: ${({ theme }) =>
            theme?.card_style?.includes('slightly-rounded') ? '12px' :
            theme?.card_style?.includes('squared') ? '8px' : '16px'};
    }
    
    .course-icon {
        position: relative;
        z-index: 2;
        width: 72px;
        height: 72px;
        background: rgba(255, 255, 255, 0.15);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 28px;
        color: white;
        backdrop-filter: blur(12px);
        border: 3px solid rgba(255, 255, 255, 0.2);
        transition: all 0.3s ease;
    }
    
    &:hover .course-icon {
        transform: scale(1.1);
        background: rgba(255, 255, 255, 0.25);
    }
`;

export const CourseTitle = styled.h3`
    position: absolute;
    bottom: 32px;
    left: 32px;
    right: 32px;
    z-index: 2;
    color: white;
    font-size: 18px;
    font-weight: 700;
    margin: 0;
    line-height: 1.3;
    text-shadow: 0 2px 12px rgba(0, 0, 0, 0.6);
    letter-spacing: -0.01em;
`;

export const CourseContent = styled.div`
    padding: 24px;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

export const CourseStatsText = styled.p`
    color: ${props => props.theme?.card_text_color || '#666'};
    font-size: 14px;
    font-weight: 500;
    margin: 0 0 16px 0;
    line-height: 1.4;
`;

export const CourseStatsRow = styled.div`
    display: flex;
    gap: 8px;
    margin-bottom: 8px;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
`;

export const StatPill = styled.div`
    background: ${props => {
        if (props.theme?.card_style?.includes('outline')) {
            return 'transparent';
        }
        return props.theme?.card_text_color ? `${props.theme.card_text_color}12` : '#f8f9fa';
    }};
    color: ${props => props.theme?.card_text_color || '#64748b'};
    border: ${props => props.theme?.card_style?.includes('outline') ? `1px solid ${props.theme?.card_text_color || '#64748b'}` : 'none'};
    padding: 8px 14px;
    border-radius: 24px;
    font-size: 13px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
    transition: all 0.2s ease;
    
    svg {
        font-size: 11px;
        opacity: 0.8;
    }
    
    &:hover {
        transform: translateY(-1px);
        background: ${props => {
            if (props.theme?.card_style?.includes('outline')) {
                return props.theme?.card_text_color ? `${props.theme.card_text_color}08` : '#f1f5f9';
            }
            return props.theme?.card_text_color ? `${props.theme.card_text_color}18` : '#e2e8f0';
        }};
    }
`;

export const StatsGroup = styled.div`
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
`;

export const PricePill = styled.div`
    background: ${props => {
        if (props.theme?.card_style?.includes('outline')) {
            return 'transparent';
        }
        return props.theme?.card_text_color ? `${props.theme.card_text_color}12` : '#f8f9fa';
    }};
    color: ${props => props.theme?.card_text_color || '#64748b'};
    border: ${props => props.theme?.card_style?.includes('outline') ? `1px solid ${props.theme?.card_text_color || '#64748b'}` : 'none'};
    padding: 8px 14px;
    border-radius: 24px;
    font-size: 13px;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
    transition: all 0.2s ease;
    
    svg {
        font-size: 11px;
        opacity: 0.8;
    }
    
    &:hover {
        transform: translateY(-1px);
        background: ${props => {
            if (props.theme?.card_style?.includes('outline')) {
                return props.theme?.card_text_color ? `${props.theme.card_text_color}08` : '#f1f5f9';
            }
            return props.theme?.card_text_color ? `${props.theme.card_text_color}18` : '#e2e8f0';
        }};
    }
`;

export const StartButton = styled.button`
    width: 100%;
    min-height: 52px;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    margin-top: auto;
    letter-spacing: -0.01em;
    
    /* Apply theme-based button styling with modern touches */
    border-radius: ${({ theme }) => {
        if (theme?.button_style?.includes('completely-rounded')) return '26px';
        if (theme?.button_style?.includes('slightly-rounded')) return '12px';
        return '0';
    }};
    
    background-color: ${({ theme }) =>
        theme?.button_style?.includes('outline') 
            ? 'transparent' 
            : theme?.button_color || '#ff9a00'};
    
    border: ${({ theme }) =>
        theme?.button_style?.includes('outline') 
            ? `2px solid ${theme?.button_color || '#ff9a00'}` 
            : 'none'};
    
    color: ${({ theme }) =>
        theme?.button_style?.includes('outline') 
            ? theme?.button_color || '#ff9a00'
            : theme?.button_text_color || 'white'};
    
    /* Enhanced shadow effects */
    box-shadow: ${({ theme }) => {
        if (theme?.button_style?.includes('outline')) return 'none';
        if (theme?.button_style?.includes('hard-shadow')) {
            const shadowColor = theme?.shadow_color || '#000000';
            return `3px 3px 0 ${shadowColor}`;
        }
        if (theme?.button_style?.includes('soft-shadow')) {
            const shadowColor = theme?.shadow_color || '#000000';
            const hexToRgba = (hex, alpha = 1) => {
                const [r, g, b] = hex.match(/\w\w/g).map(x => parseInt(x, 16));
                return `rgba(${r}, ${g}, ${b}, ${alpha})`;
            };
            return `0 6px 12px ${hexToRgba(shadowColor, 0.25)}`;
        }
        return '0 6px 16px rgba(255, 154, 0, 0.25)';
    }};
    
    &:hover {
        background-color: ${({ theme }) => {
            if (theme?.button_style?.includes('outline')) {
                return theme?.button_color || '#ff9a00';
            }
            const buttonColor = theme?.button_color || '#ff9a00';
            return buttonColor === '#ff9a00' ? '#ff8800' : buttonColor;
        }};
        
        color: ${({ theme }) => {
            if (theme?.button_style?.includes('outline')) {
                return theme?.button_text_color || 'white';
            }
            return theme?.button_text_color || 'white';
        }};
        
        transform: translateY(-2px);
        box-shadow: ${({ theme }) => {
            if (theme?.button_style?.includes('outline')) return 'none';
            if (theme?.button_style?.includes('hard-shadow')) {
                const shadowColor = theme?.shadow_color || '#000000';
                return `4px 4px 0 ${shadowColor}`;
            }
            if (theme?.button_style?.includes('soft-shadow')) {
                const shadowColor = theme?.shadow_color || '#000000';
                const hexToRgba = (hex, alpha = 1) => {
                    const [r, g, b] = hex.match(/\w\w/g).map(x => parseInt(x, 16));
                    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
                };
                return `0 8px 16px ${hexToRgba(shadowColor, 0.35)}`;
            }
            return '0 8px 24px rgba(255, 154, 0, 0.35)';
        }};
    }
    
    &:active {
        transform: translateY(-1px);
    }
`;
